'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "size", "error", "startIcon", "endIcon", "disabled", "type", "maxLength", "value", "defaultValue", "onChange"],
  _excluded2 = ["className", "required", "disabled", "children"],
  _excluded3 = ["className", "disabled", "children"],
  _excluded4 = ["className", "error", "children"];
import React, { forwardRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from "../utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Omit native `size` (number of visible chars) to replace it with our
// design-system size union ('sm' | 'md') that controls height and padding.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ---------------------------------------------------------------------------
// Variants (CVA)
// ---------------------------------------------------------------------------

var inputVariants = /*#__PURE__*/cva(
// Base: field styling + subtitle-01 typography for the input text.
// Typography is shared across all sizes per Figma spec.
'tw-cds-input-field tw-cds-font-body-02', {
  variants: {
    size: {
      sm: 'tw-cds-input-sm',
      md: 'tw-cds-input-md'
    }
  },
  defaultVariants: {
    size: 'sm'
  }
});

// ---------------------------------------------------------------------------
// Input (core component)
// ---------------------------------------------------------------------------

export var Input = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'sm' : _ref$size,
    _ref$error = _ref.error,
    error = _ref$error === void 0 ? false : _ref$error,
    StartIcon = _ref.startIcon,
    EndIcon = _ref.endIcon,
    disabled = _ref.disabled,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'text' : _ref$type,
    maxLength = _ref.maxLength,
    value = _ref.value,
    defaultValue = _ref.defaultValue,
    onChange = _ref.onChange,
    props = _objectWithoutProperties(_ref, _excluded);
  // Character count for the inline counter display.
  // Controlled: derived from `value` on every render.
  // Uncontrolled: tracked internally via onChange, initialized from `defaultValue`.
  var _useState = useState(function () {
      var _ref2;
      return String((_ref2 = value !== null && value !== void 0 ? value : defaultValue) !== null && _ref2 !== void 0 ? _ref2 : '').length;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    internalCount = _useState2[0],
    setInternalCount = _useState2[1];
  var charCount = value !== undefined ? String(value).length : internalCount;
  var handleChange = function handleChange(e) {
    if (value === undefined) {
      setInternalCount(e.target.value.length);
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };

  // Counter shows when maxLength is defined and input is not disabled.
  // Matches TextArea pattern: disabled hides the counter entirely.
  var showCounter = !disabled && maxLength !== undefined;

  // Icon color depends on state: disabled > error > default.
  // Uses semantic utility classes (not component vars) because icons
  // are standalone elements that don't need component-level theming indirection.
  var getIconColorClass = function getIconColorClass() {
    if (disabled) {
      return 'tw-cds-text-disabled';
    }
    if (error) {
      return 'tw-cds-text-feedback-alert';
    }
    return 'tw-cds-text-muted';
  };
  var iconColorClass = getIconColorClass();
  return (
    /*#__PURE__*/
    // Wrapper div — owns the visual styling (border, bg, rounded).
    // data-disabled drives CSS disabled styles on the wrapper since :disabled
    // only applies to form elements, not <div>.
    // Click handler focuses the inner <input> when clicking padding/icon areas.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- The wrapper delegates focus to the inner <input> which handles all keyboard interaction.
    _jsxs("div", {
      "data-disabled": disabled || undefined,
      className: cn(inputVariants({
        size: size
      }), error && 'tw-cds-input-error', className),
      onClick: function onClick(e) {
        var input = e.currentTarget.querySelector('input');
        if (input && document.activeElement !== input) {
          input.focus();
        }
      },
      children: [StartIcon && /*#__PURE__*/_jsx(StartIcon, {
        size: 20,
        className: cn('tw-shrink-0', iconColorClass)
      }), /*#__PURE__*/_jsx("input", _objectSpread({
        ref: ref,
        type: type,
        disabled: disabled,
        maxLength: maxLength,
        value: value,
        defaultValue: defaultValue,
        onChange: handleChange,
        className: cn('tw-flex-1 tw-min-w-0 tw-bg-transparent tw-outline-none', disabled && 'tw-cursor-not-allowed')
      }, props)), showCounter && /*#__PURE__*/_jsxs("span", {
        "aria-live": "polite",
        className: cn('tw-cds-font-body-01 tw-whitespace-nowrap tw-shrink-0', error ? 'tw-cds-text-feedback-alert' : 'tw-cds-text-secondary'),
        children: [charCount, "/", maxLength]
      }), EndIcon && /*#__PURE__*/_jsx(EndIcon, {
        size: 16,
        className: cn('tw-shrink-0', iconColorClass)
      })]
    })
  );
});
Input.displayName = 'Input';

// ---------------------------------------------------------------------------
// InputLabel
// ---------------------------------------------------------------------------

export var InputLabel = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var className = _ref3.className,
    required = _ref3.required,
    disabled = _ref3.disabled,
    children = _ref3.children,
    props = _objectWithoutProperties(_ref3, _excluded2);
  return /*#__PURE__*/_jsxs("div", {
    className: "tw-flex tw-items-center tw-gap-0.8",
    children: [/*#__PURE__*/_jsx("label", _objectSpread(_objectSpread({
      ref: ref,
      className: cn('tw-cds-font-subtitle-01', disabled ? 'tw-cds-text-disabled' : 'tw-cds-text-primary', className)
    }, props), {}, {
      children: children
    })), required && /*#__PURE__*/_jsx("span", {
      "aria-hidden": "true",
      className: cn('tw-cds-font-body-02', disabled ? 'tw-cds-text-disabled' : 'tw-cds-text-feedback-alert'),
      children: "*"
    })]
  });
});
InputLabel.displayName = 'InputLabel';

// ---------------------------------------------------------------------------
// InputDescription
// ---------------------------------------------------------------------------

export var InputDescription = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var className = _ref4.className,
    disabled = _ref4.disabled,
    children = _ref4.children,
    props = _objectWithoutProperties(_ref4, _excluded3);
  return /*#__PURE__*/_jsx("p", _objectSpread(_objectSpread({
    ref: ref,
    className: cn('tw-cds-font-body-02', disabled ? 'tw-cds-text-disabled' : 'tw-cds-text-subtle', className)
  }, props), {}, {
    children: children
  }));
});
InputDescription.displayName = 'InputDescription';

// ---------------------------------------------------------------------------
// InputMessage
// ---------------------------------------------------------------------------

// Uses body-01-md (12px medium) — smaller than InputDescription's body-02 (14px)
// per Figma spec, so the message sits visually below the description hierarchy.
export var InputMessage = /*#__PURE__*/forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
    error = _ref5.error,
    children = _ref5.children,
    props = _objectWithoutProperties(_ref5, _excluded4);
  return /*#__PURE__*/_jsx("p", _objectSpread(_objectSpread({
    ref: ref
    // role="alert" creates a live region so screen readers announce the error
    // message immediately when it appears, without requiring focus change.
    ,
    role: error ? 'alert' : undefined,
    className: cn('tw-cds-font-body-01-md', error ? 'tw-cds-text-feedback-alert' : 'tw-cds-text-subtle', className)
  }, props), {}, {
    children: children
  }));
});
InputMessage.displayName = 'InputMessage';